import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Inject,
   OnDestroy,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { PromptTypes } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { AddPromptComponent } from '../../add-prompt/containers/add-prompt.component';
import * as AddPromptActions from '../../add-prompt/state/add-prompt.actions';
import { AddClosedExceptionFormData } from '../../closed-exceptions/models/closed-exceptions.model';
import { ScheduleDetailFormService } from '../../schedule-details/services/schedule-detail-form.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [ScheduleDetailFormService],
    selector: 'ndt-add-closed-exception',
    styleUrls: ['./add-closed-exception.component.scss'],
    templateUrl: './add-closed-exception.component.html',
    standalone: false
})
export class AddClosedExceptionComponent implements FormComponent, OnDestroy {
   callState: Observable<CallState>;

   private destroyed$ = new Subject<void>();

   form: UntypedFormGroup;

   accountId: number | null;

   scheduleId: number | null;

   title: string;

   constructor(
      private dialogService: LcsDialogService,
      private actions$: Actions,
      private fb: UntypedFormBuilder,
      private changeDetectorRef: ChangeDetectorRef,
      public dialogRef: MatDialogRef<AddClosedExceptionComponent>,
      @Inject(MAT_DIALOG_DATA)
      public data: {
         accountId: number | null;
         scheduleId: number | null;
         closedExceptionFormData: AddClosedExceptionFormData;
         includeReusableOption: boolean;
      }
   ) {
      this.form = this.fb.group({
         closedExceptionId: fb.control(data.closedExceptionFormData?.closedExceptionId ?? null),
         endDate: fb.control(
            data.closedExceptionFormData?.endDate ? this.convertDate(data.closedExceptionFormData.endDate) : null,
            Validators.required
         ),
         endTime: fb.control(
            data.closedExceptionFormData?.endTime
               ? this.convertDateToTimeString(data.closedExceptionFormData.endTime)
               : null,
            Validators.required
         ),
         prompt: fb.control(data.closedExceptionFormData?.prompt ?? null),
         startDate: fb.control(
            data.closedExceptionFormData?.startDate ? this.convertDate(data.closedExceptionFormData.startDate) : null,
            Validators.required
         ),
         startTime: fb.control(
            data.closedExceptionFormData?.startTime
               ? this.convertDateToTimeString(data.closedExceptionFormData.startTime)
               : null,
            Validators.required
         ),
         schedulesToCopyTo: fb.control(data.closedExceptionFormData?.schedulesToCopyTo ?? null),
      });
      this.accountId = data.accountId;

      this.scheduleId = data.scheduleId ?? null;

      this.form.get('startTime')?.valueChanges.subscribe(() => {
         this.updateTimeRangeValidation();
      });
      this.form.get('endTime')?.valueChanges.subscribe(() => {
         this.updateTimeRangeValidation();
      });
      data.closedExceptionFormData ? (this.title = 'Edit Closed Exception') : (this.title = 'Add Closed Exception');

      this.actions$
         .pipe(ofType(AddPromptActions.createPromptSuccessNoRouteToDetail), takeUntil(this.destroyed$))
         .subscribe((action) => {
            this.form.controls['prompt'].setValue(action.promptItem);
            this.form.controls['prompt'].markAsDirty();
            this.form.controls['prompt'].updateValueAndValidity();
            this.changeDetectorRef.markForCheck();
         });
   }

   onCloseClick(): void {
      this.dialogRef.close();
   }

   onCreate(): void {
      if (this.form.valid) {
         this.dialogRef.close(this.form.value);
      }
   }

   onAddNewPromptClick(event): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
         closeOnNavigation: true,
         data: {
            accountId: this.accountId,
            includedPromptTypes: [PromptTypes.ClosedException],
            promptTypeId: PromptTypes.ClosedException,
            routeToNewPromptAfterSave: false,
            includeReusableOption: this.data.includeReusableOption,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   private convertDateToTimeString(timeString: Date): string {
      const time = new Date(timeString);
      const hours = time.getUTCHours();
      const minutes = time.getUTCMinutes();
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
   }

   private convertDate(timeString: Date): Date {
      const date = new Date(timeString);
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
   }

   private updateTimeRangeValidation(): void {
      const startTimeControl = this.form.get('startTime');
      const endTimeControl = this.form.get('endTime');

      if (startTimeControl?.value && endTimeControl?.value) {
         const isValidTimeRange = this.isOpenTimeValid();

         if (!isValidTimeRange) {
            startTimeControl?.setErrors({ invalidTimeRange: true });
            endTimeControl?.setErrors({ invalidTimeRange: true });
         } else {
            startTimeControl?.setErrors(null);
            endTimeControl?.setErrors(null);
         }
      }
   }

   private isOpenTimeValid(): boolean {
      const startTime = this.form?.get('startTime')?.value;
      const endTime = this.form?.get('endTime')?.value;
      return startTime && endTime && startTime < endTime;
   }
}
